import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { apiFetch, readApiErrorMessage } from '../lib/apiFetch';
import { useFocusTrap } from '../hooks/useFocusTrap';
import FileDropZone from './FileDropZone';
import { formatRejectedFilesMessage } from '../constants/upload';

interface UploadNewVersionModalProps {
  /** HAL `new_version` href of the record being versioned. */
  href: string;
  recordId: string;
  recordTitle?: string;
  onClose: () => void;
}

interface InitiatedIngestionProcess {
  id: string;
  uploadUrl: string | null;
  contentType: string;
  error?: string;
}

export default function UploadNewVersionModal({
  href,
  recordId,
  recordTitle,
  onClose,
}: UploadNewVersionModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, {
    onEscape: () => {
      if (!isUploading) onClose();
    },
  });

  const uploadVersion = async ([file]: File[]) => {
    if (!file || isUploading) return;
    setIsUploading(true);
    setErrorMessage(null);
    try {
      const initResponse = await apiFetch(href, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          files: [{ name: file.name, type: file.type, size: file.size, previous_version_id: recordId }],
        }),
      });
      if (!initResponse.ok) {
        throw new Error(await readApiErrorMessage(initResponse, `Upload initiation failed (${initResponse.status})`));
      }

      const initData = await initResponse.json();
      const process: InitiatedIngestionProcess | undefined = initData._embedded?.ingestion_processes?.[0];
      if (!process || process.error || !process.uploadUrl) {
        throw new Error(process?.error || 'Upload initiation failed');
      }

      const uploadResponse = await fetch(process.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': process.contentType,
          'X-Goog-Meta-pncc-processing-id': process.id,
        },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`File upload failed (${uploadResponse.status})`);
      }

      navigate(`/processing/${process.id}`, { state: { from: location.pathname } });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to upload file.');
      setIsUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => !isUploading && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-new-version-title"
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 id="upload-new-version-title" className="text-lg font-bold text-gray-900">
              Upload new version
            </h3>
            {recordTitle && (
              <p className="text-sm text-gray-500 mt-1 truncate" title={recordTitle}>
                {recordTitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500 leading-relaxed">
            The title, geographical context and classifications of the current version are suggested for the new
            version. You can review and change them before confirming.
          </p>

          {errorMessage && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {isUploading ? (
            <div className="flex flex-col items-center justify-center min-h-[320px] rounded-3xl border-2 border-dashed border-gray-200">
              <Loader2 className="w-10 h-10 text-emerald-700 animate-spin mb-4" />
              <p className="text-sm font-medium text-gray-700">Uploading file...</p>
            </div>
          ) : (
            <FileDropZone
              title="Upload Document"
              onFiles={uploadVersion}
              onRejected={(rejected) => setErrorMessage(formatRejectedFilesMessage(rejected))}
              className="min-h-[320px] px-6"
            />
          )}
        </div>
      </div>
    </div>
  );
}
